import React from "react";

// Texts of the Google Business Profile audit page. The English version is
// written for US local businesses and multi-location brands (target keyword:
// "gbp audit", secondary "google business profile audit tool" and "free google
// business profile audit"), not translated word for word.
const content = {
  fr: {
    seo: {
      title: "Audit fiche Google My Business | LocalRanker",
      description:
        "Auditez gratuitement votre fiche GMB et commencez les optimisations pour dépasser vos concurrents !",
    },
    hero: {
      title: "Outil d'audit fiche Google Business Profile",
      subtitle:
        "Analysez gratuitement votre fiche GBP en 1 clic : score d'optimisation, e-reputation, concurrence locale. Identifiez vos axes d'amélioration et dépassez vos concurrents dans le Local Pack.",
      cta: "Lancer mon audit gratuit",
    },
    search: {
      placeholder: "Nom de votre établissement",
      submit: "Analyser",
      submitting: "Analyse en cours...",
      empty: "Veuillez sélectionner un établissement",
    },
    loadingSteps: [
      "Vérification de votre fiche Google Business Profile",
      "Analyse de votre e-réputation et de vos avis",
      "Analyse de la concurrence locale",
      "Évaluation de votre positionnement local",
      "Finalisation de votre rapport d'audit",
    ],
    captcha: "Veuillez résoudre le captcha pour accéder à l'audit",
    errors: {
      tooManyRequests:
        "Vous avez atteint la limite d'essais. Réessayez plus tard ou contactez-nous.",
      notFound:
        "Établissement introuvable. Vérifiez le nom et l'adresse, puis réessayez.",
      invalidEmail:
        "Email non valide. Veuillez réessayer avec un email professionnel.",
      analysis: "Une erreur est survenue lors de l'analyse. Veuillez réessayer.",
      generic: "Une erreur est survenue. Veuillez réessayer dans quelques instants.",
      retry: "Réessayer",
    },
    modal: {
      title: "Encore une petite étape",
      submit: "Analyser",
    },
    seoSection: (demoHref) => (
      <>
        <h2>Pourquoi réaliser un audit Google Business Profile ?</h2>
        <p>
          Votre fiche Google Business Profile (anciennement Google My Business)
          est le premier point de contact entre votre établissement et vos
          futurs clients. Lorsqu'un internaute recherche un commerce, un
          restaurant ou un prestataire à proximité, Google affiche les fiches
          locales directement dans le <strong>Local Pack</strong> et sur Google
          Maps. Une fiche incomplète, mal optimisée ou négligée, c'est une
          opportunité manquée de capter du trafic qualifié vers votre point de
          vente.
        </p>
        <p>
          Notre outil d'audit GBP gratuit analyse en quelques secondes les
          critères déterminants pour votre référencement local : complétude de
          la fiche, qualité des photos, fréquence des publications,
          e-réputation, cohérence NAP (Nom, Adresse, Téléphone) et bien plus.
          Vous obtenez un score global ainsi que des recommandations concrètes
          pour améliorer votre visibilité locale.
        </p>

        <h2>Les critères analysés par notre audit SEO local</h2>
        <p>
          L'audit évalue votre fiche sur plus d'une vingtaine de critères
          regroupés en plusieurs catégories. La{" "}
          <strong>complétude de la fiche</strong> vérifie que vos informations
          essentielles sont renseignées : horaires d'ouverture, catégorie
          principale et secondaires, description de l'établissement, lien vers
          votre site web et numéro de téléphone. L'analyse de{" "}
          <strong>l'e-réputation</strong> mesure votre note moyenne, le nombre
          d'avis et la présence de réponses aux avis clients, un facteur clé pour
          convaincre de nouveaux visiteurs.
        </p>
        <p>
          L'outil vérifie également la présence de{" "}
          <strong>Google Posts</strong> récents (actualités, offres,
          événements), la qualité et la quantité de vos{" "}
          <strong>photos et vidéos</strong>, l'existence de{" "}
          <strong>produits ou services</strong> référencés, ainsi que la section{" "}
          <strong>Questions &amp; Réponses</strong>. Chaque critère est noté
          individuellement avec une observation détaillée et une recommandation
          actionnable.
        </p>

        <h2>Comment améliorer votre score d'optimisation GBP ?</h2>
        <p>
          Après avoir lancé votre audit, vous recevez un rapport complet avec les
          points forts validés et les axes d'amélioration prioritaires. Les
          actions les plus impactantes pour votre référencement local sont
          généralement : compléter toutes les informations de votre fiche,
          publier régulièrement du contenu via les Google Posts, répondre
          systématiquement aux avis (positifs comme négatifs), ajouter des photos
          récentes de qualité et activer les attributs pertinents pour votre
          activité.
        </p>
        <p>
          Pour aller plus loin,{" "}
          <a href={demoHref} target="_blank" rel="noreferrer noopener">
            Localranker
          </a>{" "}
          vous permet de gérer et d'optimiser l'ensemble de vos fiches Google
          Business Profile depuis une seule plateforme : publication
          multi-fiches, gestion centralisée des avis, suivi du positionnement
          local et rapports de performance automatisés. Que vous gériez un seul
          point de vente ou un réseau d'établissements, l'audit est la première
          étape pour reprendre le contrôle de votre présence locale sur Google.
        </p>
      </>
    ),
  },
  en: {
    seo: {
      title: "Free Google Business Profile Audit Tool | Localranker",
      description:
        "Run a free GBP audit in one click: score your Google Business Profile, see what's missing and get clear fixes to rank higher in Google Maps and local search.",
    },
    hero: {
      title: "Free Google Business Profile Audit",
      subtitle:
        "Audit your Google Business Profile in one click: get your optimization score, a review check and a look at local competition. See what to fix to outrank competitors in the Local Pack.",
      cta: "Run my free audit",
    },
    search: {
      placeholder: "Your business name",
      submit: "Audit",
      submitting: "Auditing...",
      empty: "Please select a business",
    },
    loadingSteps: [
      "Checking your Google Business Profile",
      "Analyzing your reviews and reputation",
      "Analyzing local competition",
      "Evaluating your local rankings",
      "Wrapping up your audit report",
    ],
    captcha: "Please complete the captcha to run the audit",
    errors: {
      tooManyRequests:
        "You've reached the audit limit. Please try again later or contact us.",
      notFound:
        "We couldn't find this business. Check the name and address, then try again.",
      invalidEmail: "Invalid email. Please try again with your work email.",
      analysis: "Something went wrong during the audit. Please try again.",
      generic: "Something went wrong. Please try again in a moment.",
      retry: "Try again",
    },
    modal: {
      title: "One more step",
      submit: "Run the audit",
    },
    seoSection: (demoHref) => (
      <>
        <h2>Why run a GBP audit?</h2>
        <p>
          Your Google Business Profile (formerly Google My Business) is often
          the first thing customers see of your business. When someone searches
          for a store, a restaurant or a service near them, Google shows local
          profiles right in the <strong>Local Pack</strong> and on Google Maps.
          An incomplete or neglected profile means missed calls, missed
          direction requests and missed visits.
        </p>
        <p>
          Our free Google Business Profile audit checks the factors that matter
          most for local SEO in seconds: profile completeness, categories,
          reviews and rating, Google posts, products and services, and more. You
          get an overall score plus clear recommendations to improve your local
          visibility.
        </p>

        <h2>What our Google Business Profile audit tool checks</h2>
        <p>
          The audit reviews your profile against a set of criteria grouped into
          categories. <strong>Profile completeness</strong> checks that the
          essentials are filled in: business hours, primary category,
          description, website link and phone number.{" "}
          <strong>Reviews</strong> looks at your average rating and number of
          reviews, two key factors in convincing new customers to choose you.
        </p>
        <p>
          The tool also checks for recent <strong>Google posts</strong>, listed{" "}
          <strong>products or services</strong>, the{" "}
          <strong>Q&amp;A</strong> section and linked{" "}
          <strong>social profiles</strong>. Each criterion comes with a short
          explanation and a recommendation you can act on.
        </p>

        <h2>How to improve your Google Business Profile score</h2>
        <p>
          Once your free audit is done, you get a report with what's already
          optimized and what to fix first. The changes that usually make the
          biggest difference: complete every field of your profile, post
          regularly, reply to every review (good and bad), add recent,
          high-quality photos and turn on the attributes that fit your business.
        </p>
        <p>
          Managing more than one location?{" "}
          <a href={demoHref} target="_blank" rel="noreferrer noopener">
            Localranker
          </a>{" "}
          lets you manage and optimize all your Google Business Profiles from one
          platform: bulk posting, centralized review management, local rank
          tracking and automated performance reports. Whether you run one
          location or hundreds, a GBP audit is the first step to taking control
          of your local presence on Google.
        </p>
      </>
    ),
  },
};

export default content;
